import * as React from 'react';
import { flushSync } from 'react-dom';
import { Trans, useTranslation } from 'react-i18next';
import {
  Bot,
  ChevronDown,
  ChevronUp,
  Minus,
  Eye,
  EyeOff,
  Copy,
} from 'lucide-react';
import { ChatComposer, type AgentComposerMode, type ChatComposerSubmitPayload } from './ChatComposer';
import { PlaybookPickerModal, type LinkedPlaybookSelection } from './PlaybookPickerModal';
import { SkillPicker } from './SkillPicker';
import { CustomSkillsPanel } from './CustomSkillsPanel';
import { SkillRunner } from './SkillRunner';
import { BankOfChangesRunner } from './BankOfChangesRunner';
import { CREATE_BANK_OF_CHANGES_ID } from '../../features/agent/skills/builtInSkills';
import type { AgentSkillMeta } from '../../features/agent/skills/types';
import { filesToAttachmentPayloads } from '../../utils/fileAttachments';
import { AskMarkdown } from './AskMarkdown';
import apiService from '../../services/api';
import { DocumentReader } from '../../word/api/DocumentReader';
import { ProofreadWordAdapter } from '../../features/proofread/ProofreadWordAdapter';
import { executeAgentToolCalls } from '../../features/agent/AgentWordTools';
import {
  buildAgentIssuePreviewHtml,
  liveParagraphPreviewHtml,
} from '../../features/agent/agentIssuePreview';
import { log, logError } from '../../utils/logging';
import {
  getReviewPartyContextInstruction,
  hasReviewPartyContextComplete,
  resolveTrackChangeAuthorSide,
} from '../../utils/reviewPartyContext';
import { detectTextDirection } from '../../utils/textUtils';
import { directionForLanguage } from '../../i18n/config';
import {
  formatOoxmlParagraphForModel,
  markupToAcceptedPlainText,
} from '../../utils/trackChangesModelText';
import type { AgentIssue, AgentScanStatus } from '../../features/agent/types';
import { SEVERITY_ORDER } from '../../features/agent/types';
import { RunActivity, type RunActivityCluster } from './conversation/RunActivity';
import './conversation/conversationLook.css';
import './AskView.css';
import './CrossCheckClauseCard.css';
import './AgentView.css';

const LEADING_CLAUSE_NUMBER_RE = /^\s*(\d+(?:\.\d+)*)[.\s]/;

function extractLeadingClauseNumber(text: string): string | null {
  const cleaned = text.replace(/[\u200f\u200e\u061c]/g, '');
  const leading = cleaned.match(LEADING_CLAUSE_NUMBER_RE);
  if (leading?.[1]) return leading[1];
  const labeled = cleaned.match(/סעיף\s+(\d+(?:\.\d+)*)/);
  return labeled?.[1] ?? null;
}

function uniqueIssueParagraphIndices(issues: AgentIssue[]): number[] {
  const set = new Set<number>();
  for (const issue of issues) {
    if (Number.isInteger(issue.paragraphIndex) && issue.paragraphIndex >= 0) {
      set.add(issue.paragraphIndex);
    }
  }
  return [...set];
}

let _idCounter = 0;
function nextId(): string {
  return `ag-${++_idCounter}-${Date.now()}`;
}

/** The orchestrate prompt used to force an English **Summary** title; map it to the UI language. */
function localizeAgentRoundSummaryMarkdown(md: string, summaryLabel: string): string {
  if (!md || !summaryLabel) return md;
  return md
    .replace(/^(\s*)\*\*Summary\*\*(\s*:?\s*)$/im, `$1**${summaryLabel}**$2`)
    .replace(/^(\s*#{1,3}\s*)Summary(\s*:?\s*)$/im, `$1${summaryLabel}$2`);
}

type FilterMode = 'open' | 'all';

/** One completed agent turn — kept in chronological order */
type AgentThreadRound = {
  id: string;
  userMessage: string;
  plan: string[];
  planStepCompleted: number;
  activityLog: string[];
  roundSummary: string;
  issues: AgentIssue[];
};

const MAX_ORCHESTRATION_ITERATIONS = 8;

/** Serialized prior thread for the engine to revise/extend/drop issues on follow-up */
function buildPreviousRoundsContext(rs: AgentThreadRound[]) {
  return rs.map((r) => ({
    user_message: r.userMessage,
    round_summary: r.roundSummary,
    issues: r.issues.map((i) => ({
      paragraphIndex: i.paragraphIndex,
      originalText: i.originalText,
      suggestedText: i.suggestedText,
      explanation: i.explanation,
      category: i.category,
      severity: i.severity,
      groupId: i.groupId,
      status: i.status,
    })),
  }));
}

interface AgentViewProps {
  /** Parent project ID — used to list playbooks. When absent the playbook icon is disabled. */
  parentProjectId?: string;
  /** Drives which backend endpoint is preferred for listing playbooks. */
  workflowHint?: 'construction_loan' | 'real_estate';
  onRequireReview?: () => void;
}

export const AgentView: React.FC<AgentViewProps> = ({
  parentProjectId,
  workflowHint,
  onRequireReview,
}) => {
  const { t, i18n } = useTranslation('taskpane');
  const chromeDir = directionForLanguage(i18n.language);
  const modeMarks = {
    suggestions: <strong dir="ltr" />,
    draft: <strong dir="ltr" />,
    workflows: <strong dir="ltr" />,
    skills: <strong dir="ltr" />,
  };
  const [goal, setGoal] = React.useState('');
  const [primaryGoal, setPrimaryGoal] = React.useState<string | null>(null);
  const [rounds, setRounds] = React.useState<AgentThreadRound[]>([]);
  const [scanStatus, setScanStatus] = React.useState<AgentScanStatus>('idle');
  const [scanError, setScanError] = React.useState<string | null>(null);

  /** In-flight request: narration + plan update live */
  const [liveActivityLog, setLiveActivityLog] = React.useState<string[]>([]);
  const [livePlan, setLivePlan] = React.useState<string[]>([]);
  const [livePlanStepCompleted, setLivePlanStepCompleted] = React.useState(0);
  const [pendingUserMessage, setPendingUserMessage] = React.useState<string | null>(null);

  const [filterModeByRound, setFilterModeByRound] = React.useState<Record<string, FilterMode>>({});
  const [hideInsDelByIssueId, setHideInsDelByIssueId] = React.useState<Set<string>>(() => new Set());
  const [paragraphOoxml, setParagraphOoxml] = React.useState<Record<number, string>>({});
  const [clauseNumbers, setClauseNumbers] = React.useState<Record<number, string>>({});
  const [previewHtmlByIssueId, setPreviewHtmlByIssueId] = React.useState<Record<string, string>>({});
  const [previewEpoch, setPreviewEpoch] = React.useState(0);
  const [replaceAllResult, setReplaceAllResult] = React.useState<{
    roundId: string;
    succeeded: number;
    failed: number;
  } | null>(null);

  /** true = suggestion list hidden (only for non-latest rounds; latest is always expanded) */
  const [issuesListCollapsedByRoundId, setIssuesListCollapsedByRoundId] = React.useState<
    Record<string, boolean>
  >({});

  /** Rounds marked stale as soon as the user sends a follow-up (until a newer round exists) */
  const [staleRoundIds, setStaleRoundIds] = React.useState<Set<string>>(() => new Set());

  const [typedNarration, setTypedNarration] = React.useState('');
  /** Suggestions = review + issues list. Draft = generate/insert. guided_workflow = built-in flows. skills = custom definitions. */
  const [agentMode, setAgentMode] = React.useState<AgentComposerMode>('suggestions');

  /** Active workflow when agentMode === 'guided_workflow' */
  const [activeSkill, setActiveSkill] = React.useState<AgentSkillMeta | null>(null);

  const handleAgentModeChange = React.useCallback((mode: AgentComposerMode) => {
    setAgentMode(mode);
    if (mode !== 'guided_workflow') {
      setActiveSkill(null);
    }
  }, []);

  /** Playbook picker state */
  const [playbookPickerOpen, setPlaybookPickerOpen] = React.useState(false);
  const [linkedPlaybook, setLinkedPlaybook] = React.useState<LinkedPlaybookSelection | null>(null);

  const activityEndRef = React.useRef<HTMLDivElement>(null);
  /** Thread scroll container — follow-up runs append the pending block below prior rounds; keep it in view */
  const transcriptScrollRef = React.useRef<HTMLDivElement>(null);

  const hasSubmittedOnce = scanStatus !== 'idle';
  const lastNarrationKey =
    liveActivityLog.length > 0 ? liveActivityLog[liveActivityLog.length - 1] : '';

  React.useEffect(() => {
    if (!lastNarrationKey || scanStatus !== 'scanning') {
      setTypedNarration('');
      return;
    }
    let i = 0;
    setTypedNarration('');
    const id = window.setInterval(() => {
      i += 2;
      setTypedNarration(lastNarrationKey.slice(0, i));
      if (i >= lastNarrationKey.length) {
        window.clearInterval(id);
      }
    }, 12);
    return () => window.clearInterval(id);
  }, [lastNarrationKey, scanStatus]);

  const issueIdsKey = rounds.flatMap((r) => r.issues).map((i) => i.id).join('|');

  React.useEffect(() => {
    const issues = rounds.flatMap((r) => r.issues);
    const indices = uniqueIssueParagraphIndices(issues);
    if (issues.length === 0) {
      setParagraphOoxml({});
      setClauseNumbers({});
      setPreviewHtmlByIssueId({});
      return undefined;
    }

    let cancelled = false;
    setPreviewHtmlByIssueId({});
    void (async () => {
      let ooxmlMap: Record<number, string> = {};
      try {
        ooxmlMap = await DocumentReader.getParagraphOoxmlByIndices(indices);
      } catch (err) {
        logError('[AgentView] Failed to load paragraph OOXML', err);
      }
      if (cancelled) return;
      setParagraphOoxml(ooxmlMap);

      try {
        const pairs = await Promise.all(
          indices.map(async (index) => {
            const number = await DocumentReader.getClauseNumberForParagraphIndex(index);
            return [index, number] as const;
          }),
        );
        if (cancelled) return;
        const map: Record<number, string> = {};
        for (const [index, number] of pairs) {
          if (number) map[index] = number.replace(/\.$/, '');
        }
        setClauseNumbers(map);
      } catch (err) {
        logError('[AgentView] Failed to load clause numbers', err);
        if (!cancelled) setClauseNumbers({});
      }

      for (const issue of issues) {
        if (cancelled) return;
        try {
          const html = await buildAgentIssuePreviewHtml({
            paragraphIndex: issue.paragraphIndex,
            originalText: issue.originalText,
            suggestedText: issue.suggestedText,
            ooxml: ooxmlMap[issue.paragraphIndex],
          });
          if (cancelled || !html) continue;
          setPreviewHtmlByIssueId((prev) => ({ ...prev, [issue.id]: html }));
        } catch (err) {
          logError('[AgentView] Failed to build issue preview', err);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // issueIdsKey captures which findings exist; previewEpoch refreshes after apply.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [issueIdsKey, previewEpoch]);

  React.useLayoutEffect(() => {
    if (scanStatus !== 'scanning' || !pendingUserMessage) return;
    const el = transcriptScrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [
    scanStatus,
    pendingUserMessage,
    liveActivityLog,
    typedNarration,
    livePlan,
    livePlanStepCompleted,
  ]);

  function getFilterMode(roundId: string): FilterMode {
    return filterModeByRound[roundId] ?? 'open';
  }

  function setFilterMode(roundId: string, mode: FilterMode) {
    setFilterModeByRound((prev) => ({ ...prev, [roundId]: mode }));
  }

  function toggleHideInsDel(issueId: string) {
    setHideInsDelByIssueId((prev) => {
      const next = new Set(prev);
      if (next.has(issueId)) next.delete(issueId);
      else next.add(issueId);
      return next;
    });
  }

  function togglePrevIssuesList(roundId: string) {
    setIssuesListCollapsedByRoundId((prev) => ({
      ...prev,
      [roundId]: !(prev[roundId] ?? true),
    }));
  }

  async function runScan(payload?: ChatComposerSubmitPayload) {
    if (agentMode === 'guided_workflow' || agentMode === 'skills') return;

    const trimmedGoal = (payload?.text ?? goal).trim();
    const attachmentFiles = payload?.attachments ?? [];
    if (!trimmedGoal && attachmentFiles.length === 0) {
      return;
    }
    if (scanStatus === 'scanning') return;

    if (!hasReviewPartyContextComplete()) {
      onRequireReview?.();
      return;
    }

    const newMessage =
      trimmedGoal ||
      (attachmentFiles.length
        ? t('agent.attached', { names: attachmentFiles.map((f) => f.name).join(', ') })
        : '');
    const goalForApi = primaryGoal ?? newMessage;
    const followUpForApi = primaryGoal ? newMessage : undefined;
    const priorRoundsForApi =
      rounds.length > 0 ? buildPreviousRoundsContext(rounds) : undefined;

    if (rounds.length > 0) {
      setStaleRoundIds(new Set(rounds.map((r) => r.id)));
      setIssuesListCollapsedByRoundId((prev) => {
        const next = { ...prev };
        rounds.forEach((r) => {
          next[r.id] = true;
        });
        return next;
      });
    }

    if (!primaryGoal) {
      setPrimaryGoal(newMessage);
    }

    const sentGoal = newMessage;
    setPendingUserMessage(newMessage);
    setGoal('');
    setScanStatus('scanning');
    setScanError(null);
    setReplaceAllResult(null);
    setLiveActivityLog([]);
    setLivePlan([]);
    setLivePlanStepCompleted(0);

    log('[AgentFlow] runScan:start', {
      goal: goalForApi,
      followUp: followUpForApi,
      priorRoundsCount: priorRoundsForApi?.length ?? 0,
      agentMode,
    });

    const startedAt = Date.now();

    try {
      let attachmentPayloads:
        | Awaited<ReturnType<typeof filesToAttachmentPayloads>>
        | undefined;
      if (attachmentFiles.length > 0) {
        attachmentPayloads = await filesToAttachmentPayloads(attachmentFiles);
      }

      // Whole-project playbook link: all records (author + paragraph_text), built in modal
      let playbookContext: string | undefined;
      if (linkedPlaybook?.contextBody?.trim()) {
        playbookContext = linkedPlaybook.contextBody.trim().slice(0, 200_000);
      }

      let iterationToolResults: Array<{ id: string; name: string; result?: string; error?: string }> | undefined;
      const narrationsAccum: string[] = [];
      let frozenPlan: string[] = [];
      let restructureFailureCount = 0;

      for (let iter = 0; iter < MAX_ORCHESTRATION_ITERATIONS; iter++) {
        const { paragraphs, fullText } = await DocumentReader.getFullDocumentContextForAgent(
          resolveTrackChangeAuthorSide,
        );
        log('[AgentFlow] orchestrate:context', {
          iter,
          paragraphCount: paragraphs.length,
          fullTextLength: fullText.length,
        });

        const step = await apiService.agentOrchestrateStep({
          goal: goalForApi,
          documentText: fullText,
          paragraphs,
          reviewPartyContext: getReviewPartyContextInstruction(),
          followUp: followUpForApi,
          previousRoundsContext: iter === 0 ? priorRoundsForApi : undefined,
          toolResults: iterationToolResults,
          iterationIndex: iter,
          frozenPlan: iter > 0 && frozenPlan.length > 0 ? frozenPlan : undefined,
          agentMode,
          attachments: iter === 0 ? attachmentPayloads : undefined,
          playbookContext: iter === 0 ? playbookContext : undefined,
        });

        if (step.plan?.length && frozenPlan.length === 0) {
          frozenPlan = [...step.plan];
        }

        const stablePlan = frozenPlan.length > 0 ? frozenPlan : step.plan;
        let stablePlanStep = step.planStepCompleted;
        if (stablePlan.length > 0) {
          stablePlanStep = Math.min(
            Math.max(0, stablePlanStep),
            Math.max(0, stablePlan.length - 1)
          );
        }
        // Completed round: entire plan is done (engine enforces; keep UI correct if model drifts).
        if (step.phase === 'final' && stablePlan.length > 0) {
          stablePlanStep = stablePlan.length - 1;
        }

        if (step.narration?.trim()) {
          const line = step.narration.trim();
          narrationsAccum.push(line);
        }
        flushSync(() => {
          if (step.narration?.trim()) {
            const line = step.narration.trim();
            setLiveActivityLog((prev) => [...prev, line]);
          }
          setLivePlan(stablePlan);
          setLivePlanStepCompleted(stablePlanStep);
        });

        if (step.phase === 'need_tools' && step.toolCalls.length > 0) {
          iterationToolResults = await executeAgentToolCalls(step.toolCalls);
          log('[AgentFlow] orchestrate:tools_executed', { iter, count: iterationToolResults.length });

          // If a restructure_document call reported ok:false, count consecutive failures and abort
          // after 2 tries to prevent the model from spinning on the same failing batch.
          const restructureResultProbe = iterationToolResults.find((t) => t.name === 'restructure_document');
          let restructureFailed = false;
          if (restructureResultProbe) {
            let parsedProbe: { ok?: boolean } | null = null;
            try {
              parsedProbe = restructureResultProbe.result ? JSON.parse(restructureResultProbe.result) : null;
            } catch {
              parsedProbe = null;
            }
            if (!!restructureResultProbe.error || (parsedProbe && parsedProbe.ok === false)) {
              restructureFailureCount += 1;
              restructureFailed = true;
              log('[AgentFlow] orchestrate:restructure_failure', {
                iter,
                count: restructureFailureCount,
              });
            }
          }
          if (restructureFailureCount >= 2) {
            setScanError(t('agent.restructureFailed'));
            setScanStatus('error');
            log('[AgentFlow] runScan:aborted_restructure_failures', {
              iter,
              totalMs: Date.now() - startedAt,
            });
            return;
          }

          // Fast-path: if a successful `restructure_document` was the only action and no issues
          // are pending, skip the extra LLM round trip that would just say "done" (saves ~60-90s).
          const restructureResult = iterationToolResults.find((t) => t.name === 'restructure_document');
          const onlyRestructure =
            !restructureFailed &&
            iterationToolResults.length === 1 && !!restructureResult && !restructureResult.error;
          if (onlyRestructure) {
            let parsed: { ok?: boolean; applied?: number; skipped?: number; failed?: number } | null = null;
            try {
              parsed = restructureResult.result ? JSON.parse(restructureResult.result) : null;
            } catch {
              parsed = null;
            }
            const appliedCount = parsed?.applied ?? 0;
            const skippedCount = parsed?.skipped ?? 0;
            const failedCount = parsed?.failed ?? 0;
            const allSkipped = appliedCount === 0 && skippedCount > 0 && failedCount === 0;
            // Fast-finalize whenever the batch made real progress (some ops applied)
            // or the batch is definitively a no-op (everything cleanly skipped). If
            // there were per-op failures we still fast-finalize when at least one op
            // succeeded so the user sees the partial result immediately.
            const shouldFinalize = appliedCount > 0 || allSkipped;
            if (shouldFinalize) {
              log('[AgentFlow] orchestrate:fast_finalize_after_restructure', {
                iter,
                applied: appliedCount,
                skipped: skippedCount,
                failed: failedCount,
                allSkipped,
              });
              const roundId = nextId();
              const parts: string[] = [];
              if (appliedCount > 0) parts.push(t('agent.appliedStructural', { count: appliedCount }));
              if (skippedCount > 0) parts.push(t('agent.skippedCount', { count: skippedCount }));
              if (failedCount > 0) parts.push(t('agent.failedCount', { count: failedCount }));
              const summary = allSkipped
                ? t('agent.noChangesApplied', { count: skippedCount })
                : `${parts.join(', ')}.`;
              const newRound: AgentThreadRound = {
                id: roundId,
                userMessage: newMessage,
                plan: stablePlan,
                planStepCompleted: stablePlan.length > 0 ? stablePlan.length - 1 : 0,
                activityLog: [...narrationsAccum, summary],
                roundSummary: summary,
                issues: [],
              };
              setRounds((prev) => {
                const updated = [...prev, newRound];
                setIssuesListCollapsedByRoundId(
                  Object.fromEntries(updated.map((r) => [r.id, r.id !== newRound.id]))
                );
                return updated;
              });
              setPendingUserMessage(null);
              setLiveActivityLog([]);
              setLivePlan([]);
              setScanStatus('done');
              log('[AgentFlow] runScan:done', {
                issueCount: 0,
                totalMs: Date.now() - startedAt,
                iterations: iter + 1,
                fastFinalized: true,
              });
              return;
            }
          }
          continue;
        }

        const mapped: AgentIssue[] = step.issues.map((r) => ({
          id: nextId(),
          paragraphIndex: r.paragraphIndex,
          originalText: r.originalText,
          suggestedText: r.suggestedText,
          explanation: r.explanation || '',
          category: (r.category as AgentIssue['category']) || 'clarity',
          severity: (r.severity as AgentIssue['severity']) || 'medium',
          groupId: r.groupId,
          status: 'open',
        }));

        const roundId = nextId();
        const trimmedSummary = step.roundSummary?.trim() ?? '';
        const roundSummaryText =
          trimmedSummary ||
          (mapped.length > 0
            ? t('agent.roundProduced', { count: mapped.length })
            : t('agent.roundEmpty'));

        const newRound: AgentThreadRound = {
          id: roundId,
          userMessage: newMessage,
          plan: stablePlan,
          planStepCompleted: stablePlanStep,
          activityLog: [...narrationsAccum],
          roundSummary: roundSummaryText,
          issues: mapped,
        };

        setRounds((prev) => {
          const updated = [...prev, newRound];
          setIssuesListCollapsedByRoundId(
            Object.fromEntries(updated.map((r) => [r.id, r.id !== newRound.id]))
          );
          return updated;
        });
        setPendingUserMessage(null);
        setLiveActivityLog([]);
        setLivePlan([]);
        setScanStatus('done');
        log('[AgentFlow] runScan:done', {
          issueCount: mapped.length,
          totalMs: Date.now() - startedAt,
          iterations: iter + 1,
        });
        return;
      }

      setScanError(t('agent.maxSteps'));
      setScanStatus('error');
      setGoal(sentGoal);
      setPendingUserMessage(null);
    } catch (err: unknown) {
      logError('[AgentFlow] runScan:error', {
        message: err instanceof Error ? err.message : String(err),
        totalMs: Date.now() - startedAt,
      });
      setScanError(t('agent.scanFailed'));
      setScanStatus('error');
      setGoal(sentGoal);
      setPendingUserMessage(null);
    }
  }

  function updateIssueEverywhere(issueId: string, updater: (i: AgentIssue) => AgentIssue) {
    setRounds((prev) =>
      prev.map((r) => ({
        ...r,
        issues: r.issues.map((i) => (i.id === issueId ? updater(i) : i)),
      }))
    );
  }

  function ignoreIssue(id: string) {
    updateIssueEverywhere(id, (i) => ({ ...i, status: 'ignored' }));
  }

  async function replaceIssue(id: string) {
    const flat = rounds.flatMap((r) => r.issues.map((i) => ({ roundId: r.id, issue: i })));
    const found = flat.find((x) => x.issue.id === id);
    if (!found) return;
    const { issue } = found;
    const ok = await ProofreadWordAdapter.replaceText(
      issue.originalText,
      issue.suggestedText,
      issue.paragraphIndex
    );
    if (ok) {
      updateIssueEverywhere(id, (i) => ({ ...i, status: 'resolved' }));
      setPreviewEpoch((n) => n + 1);
    }
    log('[AgentFlow] replaceIssue', {
      id,
      ok,
      paragraphIndex: issue.paragraphIndex,
      originalLen: issue.originalText?.length ?? 0,
      suggestedLen: issue.suggestedText?.length ?? 0,
    });
  }

  async function replaceAllIssuesInRound(roundId: string) {
    const round = rounds.find((r) => r.id === roundId);
    if (!round) return;
    const open = round.issues.filter((i) => i.status === 'open');
    const result = await ProofreadWordAdapter.replaceAll(
      open.map((i) => ({
        originalText: i.originalText,
        suggestion: i.suggestedText,
        paragraphIndex: i.paragraphIndex,
      }))
    );
    setReplaceAllResult({ roundId, ...result });
    if (result.succeeded > 0) {
      setRounds((prev) =>
        prev.map((r) =>
          r.id !== roundId
            ? r
            : {
                ...r,
                issues: r.issues.map((i) => (i.status === 'open' ? { ...i, status: 'resolved' } : i)),
              }
        )
      );
      setPreviewEpoch((n) => n + 1);
    }
    log('[AgentFlow] replaceAllIssuesInRound', { roundId, ...result });
  }

  function copyToClipboard(text: string) {
    try {
      navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
  }

  function visibleIssuesChronological(list: AgentIssue[], roundId: string): AgentIssue[] {
    const fm = getFilterMode(roundId);
    const filtered = list.filter((i) => fm === 'all' || i.status === 'open');
    return [...filtered].sort((a, b) => {
      const byParagraph = a.paragraphIndex - b.paragraphIndex;
      if (byParagraph !== 0) return byParagraph;
      return SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity];
    });
  }

  function renderIssueCard(issue: AgentIssue, interactive: boolean) {
    const ooxml = paragraphOoxml[issue.paragraphIndex];
    const overlayHtml = previewHtmlByIssueId[issue.id];
    const liveHtml = liveParagraphPreviewHtml(ooxml);
    const displayHtml =
      overlayHtml ||
      liveHtml ||
      ProofreadWordAdapter.buildTrackChangesHtml(issue.originalText, issue.suggestedText);
    const marked = ooxml
      ? formatOoxmlParagraphForModel(ooxml, resolveTrackChangeAuthorSide)
      : '';
    const plainParagraph =
      markupToAcceptedPlainText(marked) || marked || issue.originalText || issue.suggestedText;
    const clauseNumber =
      clauseNumbers[issue.paragraphIndex] || extractLeadingClauseNumber(plainParagraph);
    const clauseLabel = clauseNumber
      ? t('review.clauseNumber', { number: clauseNumber })
      : t('review.paragraphLabel', { number: issue.paragraphIndex + 1 });
    const paragraphDir = detectTextDirection(plainParagraph || issue.suggestedText);
    const hideMarkup = hideInsDelByIssueId.has(issue.id);

    return (
      <div
        key={issue.id}
        className={`ag-issue${issue.status !== 'open' ? ' ag-issue-resolved' : ''}`}
      >
        <div className="ag-issue-header">
          <span className="ag-issue-clause">{clauseLabel}</span>
        </div>
        {issue.explanation ? (
          <p className="ag-issue-explanation" dir="auto">
            {issue.explanation}
          </p>
        ) : null}
        <div
          className={`ag-suggested-preview finding-paragraph-preview cc-client-request-content${
            hideMarkup ? ' hide-ins-del' : ''
          }`}
          dir={paragraphDir}
        >
          <div className="cc-paragraph-xml-display" dir={paragraphDir}>
            <div
              className="cc-selected-text-xml"
              dir={paragraphDir}
              dangerouslySetInnerHTML={{ __html: displayHtml }}
            />
          </div>
        </div>
        <div
          className={`ag-actions${
            issue.status === 'open' && !interactive ? ' ag-actions--locked' : ''
          }`}
        >
          {issue.status === 'open' && interactive && (
            <button
              type="button"
              className="ag-apply-btn"
              title={t('agent.applyTitle', { suggestion: issue.suggestedText })}
              onClick={() => void replaceIssue(issue.id)}
            >
              {t('agent.apply')}
            </button>
          )}
          <button
            type="button"
            className={`ag-view-mode-btn ${hideMarkup ? 'active' : ''}`}
            onClick={() => toggleHideInsDel(issue.id)}
            title={
              hideMarkup
                ? t('review.showInsertionsAndDeletions')
                : t('review.hideInsertionsAndDeletions')
            }
            aria-pressed={hideMarkup}
          >
            <EyeOff size={14} />
            <span>{t('review.noMarkup')}</span>
          </button>
          <button
            type="button"
            className="ag-icon-btn"
            title={t('agent.navigateParagraph')}
            onClick={() =>
              void ProofreadWordAdapter.focusClauseBeforeApply(
                issue.paragraphIndex,
                issue.originalText,
              )
            }
          >
            <Eye size={12} />
          </button>
          {issue.status === 'open' && interactive && (
            <>
              <button
                type="button"
                className="ag-icon-btn"
                title={t('agent.copySuggestion')}
                onClick={() => copyToClipboard(issue.suggestedText)}
              >
                <Copy size={12} />
              </button>
              <button
                type="button"
                className="ag-icon-btn ag-ignore-btn"
                title={t('agent.ignore')}
                onClick={() => ignoreIssue(issue.id)}
              >
                <Minus size={12} />
              </button>
            </>
          )}
          {issue.status === 'open' && !interactive && (
            <span className="ag-locked-hint">{t('agent.locked')}</span>
          )}
        </div>
      </div>
    );
  }

  function renderIssueCategoriesForRound(round: AgentThreadRound, interactive: boolean) {
    const roundId = round.id;
    const list = round.issues;
    const fm = getFilterMode(roundId);
    const openCount = list.filter((i) => i.status === 'open').length;
    const totalIssues = list.length;
    const chronological = visibleIssuesChronological(list, roundId);

    return (
      <>
        <div
          className={`ag-results-header${!interactive ? ' ag-results-header--readonly' : ''}`}
          dir={chromeDir}
        >
          <div className="ag-results-header-row">
            {openCount > 0 && (
              <span className="ag-total-badge">
                {interactive
                  ? t('agent.suggestionCount', { count: openCount })
                  : t('agent.suggestionCountViewOnly', { count: openCount })}
              </span>
            )}
            {interactive && totalIssues > 0 && (
              <button
                type="button"
                className={`ag-filter-btn ${fm === 'open' ? 'active' : ''}`}
                onClick={() => setFilterMode(roundId, fm === 'open' ? 'all' : 'open')}
              >
                {fm === 'open' ? t('agent.filterOpen') : t('agent.filterAll')} <ChevronDown size={11} />
              </button>
            )}
            {interactive && openCount > 1 && (
              <button
                type="button"
                className="ag-replace-all-btn"
                onClick={() => void replaceAllIssuesInRound(roundId)}
              >
                {t('agent.applyAll')}
              </button>
            )}
          </div>
        </div>

        <div className={`ag-categories${!interactive ? ' ag-categories--readonly' : ''}`} dir={chromeDir}>
          {list.length === 0 ? (
            <div className="ag-empty">{t('agent.noSuggestions')}</div>
          ) : chronological.length === 0 ? (
            <div className="ag-empty-cat">{t('agent.allAppliedOrIgnored')}</div>
          ) : (
            <div className="ag-chronological-list">
              {chronological.map((issue) => renderIssueCard(issue, interactive))}
            </div>
          )}
        </div>

        {interactive && replaceAllResult && replaceAllResult.roundId === roundId && (
          <div className="ag-replace-summary" dir={chromeDir}>
            {t('agent.appliedCount', { count: replaceAllResult.succeeded })}
            {replaceAllResult.failed > 0 && `, ${t('agent.failedCount', { count: replaceAllResult.failed })}`}
          </div>
        )}
      </>
    );
  }

  /**
   * A round's plan and the steps the agent reported, as the one folded line every conversation of the pane
   * shows a run with: while it runs, what it is doing now; when it is over, how many steps it took. It opens
   * to the plan (done steps marked) and to the steps.
   */
  function runActivity(state: 'running' | 'done', plan: string[], planDone: number, steps: string[], now = '') {
    const clusters: RunActivityCluster[] = [];
    if (plan.length > 0) {
      clusters.push({ stage: 'plan', lines: plan.map((step, idx) => `${idx <= planDone ? '✓' : '○'} ${step}`) });
    }
    if (steps.length > 0) clusters.push({ stage: 'steps', lines: steps });
    const summary =
      state === 'running'
        ? now.trim() || (plan.length > 0 ? `${plan[Math.min(Math.max(planDone + 1, 0), plan.length - 1)]}…` : t('agent.thinking'))
        : steps.length === 1
          ? t('agent.activityDoneOne')
          : t('agent.activityDone', { n: Math.max(steps.length, plan.length) });
    return (
      <RunActivity
        state={state}
        clusters={clusters}
        summary={summary}
        toggleTitle={t('agent.activityToggle')}
        titleOf={(cluster) => (cluster.stage === 'plan' ? t('agent.plan') : t('agent.steps'))}
        rtl={chromeDir === 'rtl'}
      />
    );
  }

  const transcript = hasSubmittedOnce && (
    <div className="ag-scroll" ref={transcriptScrollRef}>
      {rounds.map((round) => {
        const latestId = rounds[rounds.length - 1]?.id;
        /** Only the current (non-stale) tail round gets full interactive suggestions */
        const isInteractiveLatest =
          round.id === latestId && !staleRoundIds.has(round.id);
        const prevIssuesCollapsed =
          !isInteractiveLatest && issuesListCollapsedByRoundId[round.id] !== false;
        const openIssuesCount = round.issues.filter((i) => i.status === 'open').length;

        const isStaleRound = staleRoundIds.has(round.id);
        const roundSummaryMd = round.roundSummary
          ? localizeAgentRoundSummaryMarkdown(round.roundSummary, t('agent.summary'))
          : '';

        return (
          <article
            key={round.id}
            className={`ag-thread-round${isStaleRound ? ' ag-thread-round--stale' : ''}`}
          >
            {isStaleRound && (
              <div className="ag-stale-banner" role="status" dir={chromeDir}>
                {t('agent.staleBanner')}
              </div>
            )}
            <div className="ag-user-bubble" dir="auto">
              {round.userMessage}
            </div>

            {(round.plan.length > 0 || round.activityLog.length > 0) && (
              <div className="ag-run" dir={chromeDir}>
                {runActivity('done', round.plan, round.planStepCompleted, round.activityLog)}
              </div>
            )}

            {roundSummaryMd ? (
              <div className="ag-round-summary-md">
                <AskMarkdown
                  content={roundSummaryMd}
                  dir={detectTextDirection(roundSummaryMd)}
                />
              </div>
            ) : null}

            {isInteractiveLatest ? (
              renderIssueCategoriesForRound(round, true)
            ) : (
              <div className={`ag-prev-issues${isStaleRound ? ' ag-prev-issues--stale' : ''}`}>
                <button
                  type="button"
                  className="ag-prev-issues-toggle"
                  dir={chromeDir}
                  onClick={() => togglePrevIssuesList(round.id)}
                  aria-expanded={!prevIssuesCollapsed}
                >
                  <span className="ag-prev-issues-label">
                    {isStaleRound ? t('agent.frozenSuggestions') : t('agent.earlierSuggestions')}
                  </span>
                  <span className="ag-prev-issues-meta" dir="auto">
                    {round.issues.length === 0
                      ? t('agent.noItems')
                      : t('agent.openTotal', { open: openIssuesCount, total: round.issues.length })}
                  </span>
                  <span className="ag-prev-issues-caret">
                    {prevIssuesCollapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
                  </span>
                </button>
                {!prevIssuesCollapsed && renderIssueCategoriesForRound(round, false)}
              </div>
            )}
          </article>
        );
      })}

      {scanStatus === 'scanning' && pendingUserMessage && (
        <article className="ag-thread-round ag-thread-round--pending">
          <div className="ag-user-bubble" dir="auto">
            {pendingUserMessage}
          </div>

          <div className="ag-run" dir={chromeDir} aria-live="polite">
            {runActivity(
              'running',
              livePlan,
              livePlanStepCompleted,
              liveActivityLog,
              typedNarration || liveActivityLog[liveActivityLog.length - 1] || '',
            )}
            <div ref={activityEndRef} />
          </div>
        </article>
      )}

      {scanStatus === 'error' && scanError && (
        <div className="ag-error" dir={chromeDir}>
          {scanError}
          <button type="button" className="ag-retry-btn" onClick={() => void runScan()}>
            {t('agent.retry')}
          </button>
        </div>
      )}
    </div>
  );

  const composerLabel = (
    <div className="ag-composer-label">
      <Bot size={14} aria-hidden />
      <span>
        {agentMode === 'draft'
          ? t('agent.labelDraft')
          : agentMode === 'guided_workflow'
            ? t('agent.labelWorkflow')
            : agentMode === 'skills'
              ? t('agent.labelSkills')
              : t('agent.labelImprove')}
      </span>
    </div>
  );

  const composer = (
    <ChatComposer
      value={goal}
      onChange={setGoal}
      onSubmit={(p) => void runScan(p)}
      disabled={
        scanStatus === 'scanning' ||
        agentMode === 'skills' ||
        (agentMode === 'guided_workflow' && !activeSkill)
      }
      submitting={scanStatus === 'scanning'}
      placeholder={
        hasSubmittedOnce
          ? agentMode === 'draft'
            ? t('agent.placeholderFollowUpDraft')
            : t('agent.placeholderFollowUp')
          : agentMode === 'draft'
            ? t('agent.placeholderDraft')
            : t('agent.placeholderImprove')
      }
      layout={hasSubmittedOnce ? 'sticky' : 'initial'}
      label={!hasSubmittedOnce ? composerLabel : undefined}
      followUp={hasSubmittedOnce}
      submitTitle={t('agent.submitTitle')}
      agentMode={agentMode}
      onAgentModeChange={handleAgentModeChange}
      onPlaybookIconClick={
        agentMode === 'guided_workflow' || agentMode === 'skills'
          ? undefined
          : () => setPlaybookPickerOpen(true)
      }
      selectedPlaybookLabel={
        linkedPlaybook
          ? t('agent.playbookChip', {
              label: linkedPlaybook.projectLabel,
              count: linkedPlaybook.recordCount,
            })
          : undefined
      }
      onClearPlaybook={() => setLinkedPlaybook(null)}
    />
  );

  return (
    <>
      <div className={`agent-view boc-chat${hasSubmittedOnce ? ' agent-view--thread' : ''}`}>
        {agentMode === 'guided_workflow' ? (
          activeSkill ? (
            // SkillRunner is the email-merge wizard, not a generic runner — a
            // workflow with its own conversation mounts its own component.
            activeSkill.id === CREATE_BANK_OF_CHANGES_ID ? (
              <BankOfChangesRunner skill={activeSkill} onBack={() => setActiveSkill(null)} />
            ) : (
              <SkillRunner skill={activeSkill} onBack={() => setActiveSkill(null)} />
            )
          ) : (
            <>
              {composer}
              <SkillPicker onSelect={setActiveSkill} />
            </>
          )
        ) : agentMode === 'skills' ? (
          <>
            {composer}
            <CustomSkillsPanel />
          </>
        ) : !hasSubmittedOnce ? (
          <>
            {composer}
            <div className="ag-idle" dir={chromeDir}>
              <Bot size={32} className="ag-idle-icon" />
              <p>
                <Trans t={t} i18nKey="agent.idleBody" components={modeMarks} />
              </p>
              <p className="ag-idle-hint">{t('agent.idleHint')}</p>
            </div>
          </>
        ) : (
          <>
            {transcript}
            {composer}
          </>
        )}
      </div>

      {playbookPickerOpen && (
        <PlaybookPickerModal
          preferredProjectId={parentProjectId}
          workflowHint={workflowHint}
          onSelect={(sel) => setLinkedPlaybook(sel)}
          onClose={() => setPlaybookPickerOpen(false)}
        />
      )}
    </>
  );
};
